import { useState, useEffect } from 'react';

/**
 * Hook that provides a local input value synced with a URL search param.
 * Only updates the URL param when the user explicitly submits (Enter key or button).
 * submitSearch can be called with an explicit value to avoid stale-closure issues
 * (e.g. when the value comes from a child component's internal state).
 */
const useDebouncedSearch = (searchParams, setSearchParams, paramKey = 'q', _delay = 400) => {
  const paramValue = searchParams.get(paramKey) || '';
  const [inputValue, setInputValue] = useState(paramValue);

  // Sync local state when URL param changes externally (e.g. "Clear Filters")
  useEffect(() => {
    setInputValue(paramValue);
  }, [paramValue]);

  // overrideValue lets callers pass the value directly, avoiding stale closures
  // when state hasn't re-rendered yet (e.g. after setSearchInput inside onSearch).
  const submitSearch = (overrideValue) => {
    const value = overrideValue !== undefined ? overrideValue : inputValue;
    const newParams = new URLSearchParams(searchParams);
    if (typeof value === 'string' && value.trim()) {
      newParams.set(paramKey, value.trim());
    } else {
      newParams.delete(paramKey);
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  return [inputValue, setInputValue, submitSearch];
};

export default useDebouncedSearch;
